import React, { useEffect, useState } from 'react';
import MainLayout from '../layouts/MainLayout';
import { 
    Box, 
    Button, 
    CircularProgress, 
    Typography,
    Card,
    Alert,
    Grid
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ProjectTable from "../components/ProjectTable";
import ProjectModal from "../components/ProjectModal";
import { getProjects } from '../services/projectService';
import { Project } from "../interfaces/Project";
import { 
    Assignment,
    CheckCircle,
    Pending,
    Error,
    TrendingUp
} from '@mui/icons-material';

const ProjectManagement: React.FC = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedProject, setSelectedProject] = useState<Project | null>(null);
    const [projects, setProjects] = useState<Project[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchProjects = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getProjects();
            setProjects(response.data as Project[]);
        } catch (error) {
            setError("Erreur lors de la récupération des projets");
            console.error('Erreur:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProjects();
    }, []);

    const handleCreateProject = () => {
        setSelectedProject(null);
        setIsModalOpen(true);
    };

    const handleEditProject = (project: Project) => {
        setSelectedProject(project);
        setIsModalOpen(true);
    };

    const getProjectStats = () => {
        const total = projects.length;
        const completed = projects.filter(project => project.status === 1).length;
        const inProgress = projects.filter(project => project.status === 2).length;
        const pending = projects.filter(project => project.status === 3).length;
        const success = projects.filter(project => project.status === 4).length;

        return { total, completed, inProgress, pending, success };
    };

    const stats = getProjectStats();

    return (
        <MainLayout>
            <Box className="p-6 space-y-6">
                {/* En-tête */}
                <Card className="bg-white/80 backdrop-blur-sm p-6">
                    <Box className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                            <Typography variant="h4" className="text-emerald-800 font-bold mb-2">
                                Gestion des projets
                            </Typography>
                            <Typography variant="body1" className="text-emerald-600">
                                Gérez et suivez vos projets
                            </Typography>
                        </div>
                        <Button
                            variant="contained"
                            onClick={handleCreateProject}
                            startIcon={<AddIcon />}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm hover:shadow-md transition-all duration-200 whitespace-nowrap"
                            sx={{
                                borderRadius: '8px',
                                textTransform: 'none',
                                px: 3,
                                py: 1
                            }}
                        >
                            Créer un projet
                        </Button>
                    </Box>
                </Card>

                {/* Messages d'erreur */}
                {error && (
                    <Alert 
                        severity="error" 
                        className="rounded-lg border border-red-100"
                        onClose={() => setError(null)}
                    >
                        {error}
                    </Alert>
                )}

                {/* Statistiques */}
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} lg={2.4}>
                        <Card className="bg-white/80 backdrop-blur-sm p-4 h-full">
                            <Box className="flex items-center justify-between">
                                <div>
                                    <Typography className="text-gray-600 text-sm">Total</Typography>
                                    <Typography variant="h4" className="font-bold text-gray-800">
                                        {stats.total}
                                    </Typography>
                                </div>
                                <Assignment className="text-gray-400 text-3xl" />
                            </Box>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2.4}>
                        <Card className="bg-white/80 backdrop-blur-sm p-4 h-full">
                            <Box className="flex items-center justify-between">
                                <div>
                                    <Typography className="text-emerald-600 text-sm">Terminés</Typography>
                                    <Typography variant="h4" className="font-bold text-emerald-800">
                                        {stats.completed}
                                    </Typography>
                                </div>
                                <CheckCircle className="text-emerald-400 text-3xl" />
                            </Box>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2.4}>
                        <Card className="bg-white/80 backdrop-blur-sm p-4 h-full">
                            <Box className="flex items-center justify-between">
                                <div>
                                    <Typography className="text-blue-600 text-sm">En cours</Typography>
                                    <Typography variant="h4" className="font-bold text-blue-800">
                                        {stats.inProgress}
                                    </Typography>
                                </div>
                                <Pending className="text-blue-400 text-3xl" />
                            </Box>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2.4}>
                        <Card className="bg-white/80 backdrop-blur-sm p-4 h-full">
                            <Box className="flex items-center justify-between">
                                <div>
                                    <Typography className="text-amber-600 text-sm">En attente</Typography>
                                    <Typography variant="h4" className="font-bold text-amber-800">
                                        {stats.pending}
                                    </Typography>
                                </div>
                                <Error className="text-amber-400 text-3xl" />
                            </Box>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2.4}>
                        <Card className="bg-white/80 backdrop-blur-sm p-4 h-full">
                            <Box className="flex items-center justify-between">
                                <div>
                                    <Typography className="text-purple-600 text-sm">Performance</Typography>
                                    <Typography variant="h4" className="font-bold text-purple-800">
                                        {stats.success}
                                    </Typography>
                                </div>
                                <TrendingUp className="text-purple-400 text-3xl" />
                            </Box>
                        </Card>
                    </Grid>
                </Grid>

                {/* Table des projets */}
                <Card className="bg-white/80 backdrop-blur-sm overflow-hidden">
                    {loading ? (
                        <Box className="flex justify-center items-center p-12">
                            <CircularProgress className="text-emerald-600" />
                        </Box>
                    ) : (
                        <Box className="overflow-x-auto">
                            <ProjectTable
                                projects={projects}
                                onEditProject={handleEditProject}
                                refreshProjects={fetchProjects}
                            />
                        </Box>
                    )}
                </Card>

                {/* Modal de création/édition */}
                <ProjectModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSave={() => {
                        setIsModalOpen(false);
                        fetchProjects();
                    }}
                    project={selectedProject}
                />
            </Box>
        </MainLayout>
    );
};

export default ProjectManagement;